// TODO: Need to move this file to a more common location for DeckManagement, as some of these constants are used
//       outside of Page/SidePanel. Leaving this file here for now to prevent issues/conflicts with upcoming milestone.

import { CodeOrDescription, DenominatorLabelValues, KPIDrivers } from '@services/api/deckManagement/graphql/schema/API';
import { isNil } from 'lodash';
import {
  BaseKpiDimensionFieldTypes,
  KpiDimension,
  KpiDimensionConfig,
  KpiDimensionConfigMap,
  PageSidePanelDropDown,
} from 'types/PageSidePanel.types';

/**
 * Number of spaces per indent level for KPI name indentation rendering and export.
 */
export const KPI_NAME_INDENT_SPACES = 4;

/**
 * For pages with empty page drivers, these should be used as the default
 * (bmillerv@ on 2025-10-27)
 */
export const DEFAULT_PAGE_DRIVERS: KPIDrivers = {
  __typename: 'KPIDrivers',
  account: null,
  account_hierarchy: null,
  location: 'LTOT',
  location_hierarchy: 'COGNOS_LOCATION_HIERARCHY',
  costcenter: 'CICC',
  costcenter_hierarchy: 'COGNOS_COST CENTER CIOS_HIERARCHY',
  company: 'TC',
  company_hierarchy: 'COGNOS_COMPANY_HIERARCHY',
  project: 'TTOT',
  project_hierarchy: 'COGNOS_PROJECT_HIERARCHY',
  channel: 'VTOT',
  channel_hierarchy: 'COGNOS_CHANNEL_HIERARCHY',
  product: 'PTOT',
  product_hierarchy: 'COGNOS_PRODUCT_HIERARCHY',
  costtype: 'All CostType',
  costpool: 'All CostPool',
  datasource: 'DS001',
  datasource_hierarchy: 'COGNOS_DATASOURCE_CORP_HIERARCHY',
  revgp: 'L10000',
  revgp_hierarchy: 'COGNOS_REVGP_SEGMENT_HIERARCHY',
};

/**
 * Available denominator label options for percentage calculations.
 */
export const DenominatorLabels: Record<DenominatorLabelValues, string> = {
  [DenominatorLabelValues.REVENUE]: '% Revenue',
  [DenominatorLabelValues.GROSS_MARGIN]: '% Gross Margin',
  [DenominatorLabelValues.OPERATING_MARGIN]: 'Operating Margin',
};

/**
 * Deckline Types which are excluded from having Denominator Configuration
 */
export const EXCLUDED_DECKLINES_FOR_DENOMINATORS: PageSidePanelDropDown[] = [
  PageSidePanelDropDown.NONE,
  PageSidePanelDropDown.BLANK_LINE,
];

/**
 * Maps page names to their default denominator labels for percentage calculations.
 * Used to automatically set the appropriate denominator when a Page is loaded without a value for `denominator_label`.
 * @see {@link PageContextProvider} (`setCurrentGranularityPage()` callback)
 * @todo Can remove these later. Leaving this in because Pages are not loaded into Auriga with a value
 *       for `denominator_label` but decklines still have values for denominator kpis.
 */
export const PAGE_NAME_DENOMINATOR_LABEL_MAPPING: { [key: string]: DenominatorLabelValues } = {
  Gross_Profit: DenominatorLabelValues.GROSS_MARGIN,
  Gross_Profit_Ex_Ads: DenominatorLabelValues.GROSS_MARGIN,
  Opex_Variable: DenominatorLabelValues.REVENUE,
  Opex_Fixed: DenominatorLabelValues.REVENUE,
  Operating_Income: DenominatorLabelValues.OPERATING_MARGIN,
  Ads_Operating_Income: DenominatorLabelValues.OPERATING_MARGIN,
  Operating_Income_Ex_Ads: DenominatorLabelValues.OPERATING_MARGIN,
  Ads_Summary: DenominatorLabelValues.REVENUE,
};

export const REPORTING_CURRENCY_VALUE = 'USD';

/**
 * Deckline Dimension Render Configuration. NOTE: Order does NOT matter here.
 * Use {@link KPI_DIMENSION_RENDER_LIST} for rendering order in the side panel.
 */
export const KPI_DIMENSION_CONFIG_MAP: KpiDimensionConfigMap = {
  [KpiDimension.ACCOUNT]: {
    key: KpiDimension.ACCOUNT,
    formulaKey: 'Account',
    label: 'Account',
    baseKpiField: {
      type: BaseKpiDimensionFieldTypes.DimensionPicker,
      dimensionName: 'account',
    },
    decklineDrivers: {
      hierarchyAccessor: 'account_hierarchy',
      valueAccessor: 'account',
    },
  },
  [KpiDimension.REVGP]: {
    key: KpiDimension.REVGP,
    formulaKey: 'REVGP',
    label: 'RevGP',
    baseKpiField: {
      type: BaseKpiDimensionFieldTypes.DimensionPicker,
      dimensionName: 'revgp',
    },
    decklineDrivers: {
      hierarchyAccessor: 'revgp_hierarchy',
      valueAccessor: 'revgp',
    },
  },
  [KpiDimension.COST_CENTER]: {
    key: KpiDimension.COST_CENTER,
    formulaKey: 'Costcenter',
    label: 'Cost Center',
    baseKpiField: {
      type: BaseKpiDimensionFieldTypes.DimensionPicker,
      dimensionName: 'costcenter',
    },
    decklineDrivers: {
      hierarchyAccessor: 'costcenter_hierarchy',
      valueAccessor: 'costcenter',
    },
  },
  [KpiDimension.COMPANY]: {
    key: KpiDimension.COMPANY,
    formulaKey: 'Company',
    label: 'Company',
    baseKpiField: {
      type: BaseKpiDimensionFieldTypes.DimensionPicker,
      dimensionName: 'company',
    },
    decklineDrivers: {
      hierarchyAccessor: 'company_hierarchy',
      valueAccessor: 'company',
    },
  },
  [KpiDimension.PROJECT]: {
    key: KpiDimension.PROJECT,
    formulaKey: 'Project',
    label: 'Project',
    baseKpiField: {
      type: BaseKpiDimensionFieldTypes.DimensionPicker,
      dimensionName: 'project',
    },
    decklineDrivers: {
      hierarchyAccessor: 'project_hierarchy',
      valueAccessor: 'project',
    },
  },
  [KpiDimension.REPORTING_CURRENCY]: {
    key: KpiDimension.REPORTING_CURRENCY,
    label: 'Reporting Currency',
    baseKpiField: {
      type: BaseKpiDimensionFieldTypes.ReadOnly,
      staticValue: REPORTING_CURRENCY_VALUE,
    },
  },
  [KpiDimension.CHANNEL]: {
    key: KpiDimension.CHANNEL,
    formulaKey: 'Channel',
    label: 'Channel',
    baseKpiField: {
      type: BaseKpiDimensionFieldTypes.DimensionPicker,
      dimensionName: 'channel',
    },
    decklineDrivers: {
      hierarchyAccessor: 'channel_hierarchy',
      valueAccessor: 'channel',
    },
  },
  [KpiDimension.PRODUCT]: {
    key: KpiDimension.PRODUCT,
    formulaKey: 'Product',
    label: 'Product',
    baseKpiField: {
      type: BaseKpiDimensionFieldTypes.DimensionPicker,
      dimensionName: 'product',
    },
    decklineDrivers: {
      hierarchyAccessor: 'product_hierarchy',
      valueAccessor: 'product',
    },
  },
  [KpiDimension.COST_TYPE]: {
    key: KpiDimension.COST_TYPE,
    formulaKey: 'CostType',
    label: 'Cost Type',
    baseKpiField: {
      type: BaseKpiDimensionFieldTypes.Select,
      options: [{ value: 'All CostType' }, { value: 'F' }, { value: 'V' }],
      rootValue: 'All CostType',
    },
    decklineDrivers: {
      valueAccessor: 'costtype',
    },
  },
  [KpiDimension.COST_POOL]: {
    key: KpiDimension.COST_POOL,
    formulaKey: 'CostPool',
    label: 'Cost Pool',
    baseKpiField: {
      type: BaseKpiDimensionFieldTypes.Select,
      options: [{ value: 'All CostPool' }, { value: 'DIGMUSVIDLF-1' }, { value: 'INTL-07' }],
      rootValue: 'All CostPool',
    },
    decklineDrivers: {
      valueAccessor: 'costpool',
    },
  },
  [KpiDimension.DATA_SOURCE]: {
    key: KpiDimension.DATA_SOURCE,
    formulaKey: 'Datasource',
    label: 'Data Source',
    baseKpiField: {
      type: BaseKpiDimensionFieldTypes.DimensionPicker,
      dimensionName: 'datasource',
    },
    decklineDrivers: {
      hierarchyAccessor: 'datasource_hierarchy',
      valueAccessor: 'datasource',
    },
  },
  [KpiDimension.LOCATION]: {
    key: KpiDimension.LOCATION,
    formulaKey: 'Location',
    label: 'Location',
    baseKpiField: {
      type: BaseKpiDimensionFieldTypes.DimensionPicker,
      dimensionName: 'location',
    },
    decklineDrivers: {
      hierarchyAccessor: 'location_hierarchy',
      valueAccessor: 'location',
    },
  },
};

/**
 * The correct order to render deckline dimensions defined in {@link KPI_DIMENSION_CONFIG_MAP}.
 * 1. Order matters.
 * 2. If you do not want to render a dimension, do not include it here.
 * @see {@link PageSidePanel} for originally-intended usage.
 */
export const KPI_DIMENSION_RENDER_LIST: KpiDimensionConfig[] = [
  KPI_DIMENSION_CONFIG_MAP[KpiDimension.ACCOUNT],
  KPI_DIMENSION_CONFIG_MAP[KpiDimension.REVGP],
  KPI_DIMENSION_CONFIG_MAP[KpiDimension.COST_CENTER],
  KPI_DIMENSION_CONFIG_MAP[KpiDimension.COMPANY],
  KPI_DIMENSION_CONFIG_MAP[KpiDimension.PROJECT],
  KPI_DIMENSION_CONFIG_MAP[KpiDimension.REPORTING_CURRENCY],
  KPI_DIMENSION_CONFIG_MAP[KpiDimension.CHANNEL],
  KPI_DIMENSION_CONFIG_MAP[KpiDimension.PRODUCT],
  KPI_DIMENSION_CONFIG_MAP[KpiDimension.COST_TYPE],
  KPI_DIMENSION_CONFIG_MAP[KpiDimension.COST_POOL],
  KPI_DIMENSION_CONFIG_MAP[KpiDimension.DATA_SOURCE],
  KPI_DIMENSION_CONFIG_MAP[KpiDimension.LOCATION],
];

/**
 * Maintains a list of dimensions that are used by KPI_DIMENSION_RENDER_LIST and supported by the DimSum/DimensionPicker.
 * This is used to filter out dimensions that are not supported by DimSum.
 * @see {@link KPI_DIMENSION_RENDER_LIST} for the list of dimensions used by the side panel.
 * @see {@link DeckContextProvider} for initial intended usage
 */
export const KPI_SUPPORTED_DIMSUM_DIMENSIONS = KPI_DIMENSION_RENDER_LIST.map((dimensionConfig) =>
  dimensionConfig.baseKpiField.type === BaseKpiDimensionFieldTypes.DimensionPicker
    ? dimensionConfig.baseKpiField.dimensionName
    : null,
).filter((dimensionName) => !isNil(dimensionName));

/**
 * Display mode options for segments in dropdown menus.
 * Maps user-friendly labels to CodeOrDescription enum values.
 */
export const SEGMENTS_DISPLAY_MODES = {
  'Code + Desc': CodeOrDescription.BOTH,
  'Code Only': CodeOrDescription.CODE,
  'Desc Only': CodeOrDescription.DESCRIPTION,
};
